import { useId, useState, type FormEvent } from 'react'
import {
  currencySymbol,
  withBarcode,
  type CatalogItem,
  type CatalogItemInputData,
  type FxRates,
  type ItemHistory,
  type Offer,
  type Store,
} from '@sp/shared'
import { api } from '../lib/api.ts'
import { productName } from '../lib/barcode.ts'
import { HttpError } from '../lib/http.ts'
import { formatQty, parsePriceInput, parseQtyInput, priceInputValue, storeSub } from '../lib/format.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Sheet } from './Sheet.tsx'
import { PriceHistory } from './PriceHistory.tsx'
import { BarcodeScanner } from './BarcodeScanner.tsx'
import { Icon } from './Icon.tsx'
import { ItemOffers } from './ItemOffers.tsx'
import { Button, Chip, Paragraph, SelectField, Stepper, TextField } from './ui.tsx'
import { ConfirmSheet } from './ConfirmSheet.tsx'

interface Draft {
  name: string
  storeId: string
  qty: string
  unit: string
  price: string
  category: string
  offerTerms: string
}

function draftOf(item: CatalogItem | null, defaultStoreId: string): Draft {
  return {
    name: item?.name ?? '',
    storeId: item ? (item.storeId ?? '') : defaultStoreId,
    qty: item ? String(item.defaultQty).replace('.', ',') : '1',
    unit: item?.unit ?? '',
    price: priceInputValue(item?.typicalPriceMinor ?? null),
    category: item?.category ?? '',
    offerTerms: item?.offerTerms ?? '',
  }
}

interface FormProps {
  groupId: string
  /** Obchody na vyber — vcetne archivovaneho, na ktery polozka uz ukazuje. */
  stores: readonly Store[]
  /** null = nova polozka */
  item: CatalogItem | null
  defaultStoreId: string
  onSaved(item: CatalogItem): void
  onArchived(id: string): void
  /** Po ulozeni nebo odebrani — panel se zavre, panel na pocitaci zustava. */
  onDone(): void
  /** Nakupy teto polozky z dokoncenych nakupu. */
  history?: ItemHistory | undefined
  /** Odebere zbozi jen z otevrene sablony, ve skupine zustane. */
  onRemoveFromTemplate?: (() => void) | undefined
  /** Nazev otevrene sablony na tlacitku „Odebrat z …". */
  templateName?: string | undefined
  /** Sablona jina nez Pravidelny nakup: mnozstvi v ni (obvykle mnozstvi je pak pod „Víc"). */
  templateQty?: { value: number; onChange(qty: number): void } | undefined
  /** Akce polozky ze vsech obchodu skupiny — nahore seznam po obchodech. */
  offers?: { list: readonly Offer[]; rates: FxRates; thumbs: boolean; onPick(offer: Offer): void } | undefined
}

/**
 * Formular polozky sablony. Na telefonu v panelu (CatalogItemSheet), na pocitaci
 * v souhrnu vedle tabulky. Koncept se plni jen pri pripojeni — rodic predava
 * key podle polozky, at se pri prepnuti na jinou polozku naplni znovu.
 */
export function CatalogItemForm({
  groupId,
  stores,
  item,
  defaultStoreId,
  onSaved,
  onArchived,
  onDone,
  history,
  onRemoveFromTemplate,
  templateName,
  templateQty,
  offers,
}: FormProps) {
  const termsId = useId()
  const [more, setMore] = useState(item === null)
  const [deleting, setDeleting] = useState(false)
  const [draft, setDraft] = useState<Draft>(() => draftOf(item, defaultStoreId))
  const [codes, setCodes] = useState<string[]>(() => item?.barcodes ?? [])
  const [scanning, setScanning] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const bind = (key: keyof Draft) => (e: { target: { value: string } }) =>
    setDraft((prev) => ({ ...prev, [key]: e.target.value }))
  const store = stores.find((s) => s.id === draft.storeId) ?? null

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(err instanceof HttpError && err.isOffline ? 'Šablonu jde měnit jen se signálem.' : errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    const name = draft.name.trim()
    const qty = parseQtyInput(draft.qty)
    const price = parsePriceInput(draft.price)
    if (!name) return setError('Chybí název')
    if (qty === null) return setError('Množství musí být kladné číslo')
    if (price === 'invalid') return setError('Cena musí mít tvar 4,99')
    if (price !== null && !store) return setError('Cena potřebuje obchod – podle něj se pozná měna')
    // Posilaji se vsechna pole, i pri uprave — patch s jednim polem uz
    // jednou tise smazal zbytek polozky.
    const input: CatalogItemInputData = {
      name,
      storeId: store?.id ?? null,
      defaultQty: qty,
      unit: draft.unit.trim() || null,
      category: draft.category.trim() || null,
      typicalPriceMinor: price,
      typicalCurrency: price === null ? null : (store?.currency ?? null),
      offerTerms: draft.offerTerms.trim() || null,
      barcodes: codes,
    }
    void run(async () => {
      onSaved(item ? await api.catalog.update(item.id, input) : await api.catalog.create(groupId, input))
      onDone()
    })
  }

  function scanned(code: string) {
    setScanning(false)
    setCodes((prev) => withBarcode(prev, code))
    // U nove polozky bez nazvu napovi nazev otevrena databaze vyrobku.
    if (!draft.name.trim()) {
      void productName(code).then((name) => {
        if (name) setDraft((prev) => (prev.name.trim() ? prev : { ...prev, name }))
      })
    }
  }

  function archive() {
    if (!item) return
    setDeleting(false)
    void run(async () => {
      await api.catalog.archive(item.id)
      onArchived(item.id)
      onDone()
    })
  }

  return (
    <form className="stack" onSubmit={submit}>
      {item && offers && (
        <ItemOffers
          offers={offers.list}
          store={store}
          rates={offers.rates}
          terms={item.offerTerms ?? item.name}
          thumbs={offers.thumbs}
          onPick={offers.onPick}
          onEditTerms={() => {
            setMore(true)
            // Pole je v „Víc", ktere se prave rozbaluje.
            requestAnimationFrame(() => {
              const input = document.getElementById(termsId)
              input?.scrollIntoView({ block: 'center', behavior: 'smooth' })
              input?.focus({ preventScroll: true })
            })
          }}
        />
      )}
      <TextField label="Název" value={draft.name} onChange={bind('name')} maxLength={200} />
      <div className="field-pair">
        <SelectField
          label="Obchod"
          value={draft.storeId}
          onChange={(storeId) => setDraft((prev) => ({ ...prev, storeId }))}
          options={[
            { value: '', label: 'Bez obchodu' },
            ...stores.map((s) => ({ value: s.id, label: s.name, sub: storeSub(s) })),
          ]}
        />
        <TextField
          label={`Cena${store ? ` (${currencySymbol(store.currency)})` : ''}`}
          value={draft.price}
          onChange={bind('price')}
          inputMode="decimal"
          placeholder="nepovinné"
        />
      </div>
      {templateQty ? (
        <div className="qty-row">
          <span>Množství</span>
          <Stepper
            value={formatQty(templateQty.value, draft.unit.trim() || null)}
            decrementLabel={`Méně: ${draft.name}`}
            incrementLabel={`Více: ${draft.name}`}
            onDecrement={() => templateQty.onChange(templateQty.value - 1)}
            onIncrement={() => templateQty.onChange(templateQty.value + 1)}
          />
        </div>
      ) : (
        <TextField label="Množství" value={draft.qty} onChange={bind('qty')} inputMode="decimal" />
      )}
      <button type="button" className="disclosure" aria-expanded={more} onClick={() => setMore((v) => !v)}>
        <span>{more ? 'Méně' : 'Víc: jednotka, kategorie, hledání v akcích, kódy, ceny'}</span>
        <Icon name={more ? 'chevron_up' : 'chevron_down'} size={20} />
      </button>
      {more && (
        <div className="stack">
          <div className="field-pair">
            <TextField label="Jednotka" value={draft.unit} onChange={bind('unit')} maxLength={20} placeholder="ks" />
            {templateQty && (
              <TextField label="Obvyklé množství" value={draft.qty} onChange={bind('qty')} inputMode="decimal" />
            )}
          </div>
          <TextField
            label="Kategorie"
            value={draft.category}
            onChange={bind('category')}
            maxLength={80}
            placeholder="nepovinné, třeba Mléčné"
          />
          <TextField
            label="Hledat v akcích jako"
            hint="Názvy z letáků oddělené čárkou. Bez nich se hledá podle názvu, u obchodů v Německu a Polsku i v překladu."
            id={termsId}
            value={draft.offerTerms}
            onChange={bind('offerTerms')}
            maxLength={300}
            placeholder="třeba Butter, Masło"
          />
          <div className="field">
            <span className="field-label">Čárové kódy</span>
            {codes.length > 0 && (
              <div className="code-list">
                {codes.map((code) => (
                  <Chip
                    key={code}
                    className="code-chip"
                    aria-label={`Odebrat kód ${code}`}
                    onClick={() => setCodes((prev) => prev.filter((c) => c !== code))}
                  >
                    {code}
                    <Icon name="x" size={14} />
                  </Chip>
                ))}
              </div>
            )}
            <Button compact icon="barcode" className="self-start" onClick={() => setScanning(true)}>
              Naskenovat kód
            </Button>
            <span className="field-hint">V obchodě pak stačí kód naskenovat a položka se na seznamu sama najde.</span>
          </div>
          {history && <PriceHistory history={history} />}
        </div>
      )}
      <BarcodeScanner
        open={scanning}
        onClose={() => setScanning(false)}
        onCode={scanned}
        title="Kód položky"
        hint={draft.name.trim() || undefined}
      />
      {error && <Paragraph tone="error">{error}</Paragraph>}
      <Button kind="primary" type="submit" full disabled={busy}>
        {item ? 'Uložit' : 'Přidat do šablony'}
      </Button>
      {item && (
        <div className="button-row wrap-row">
          {onRemoveFromTemplate && (
            <Button compact disabled={busy} onClick={onRemoveFromTemplate}>
              {templateName?.startsWith('Pravidelný') ? 'Odebrat z Pravidelného' : 'Odebrat ze šablony'}
            </Button>
          )}
          <Button kind="danger" compact disabled={busy} onClick={() => setDeleting(true)}>
            Smazat úplně
          </Button>
        </div>
      )}
      {item && (
        <ConfirmSheet
          open={deleting}
          title={`Smazat „${item.name}“?`}
          confirmLabel="Smazat ze všech šablon"
          danger
          onConfirm={archive}
          onClose={() => setDeleting(false)}
        >
          Zmizí ze všech šablon skupiny. Staré nákupy zůstanou beze změny.
        </ConfirmSheet>
      )}
    </form>
  )
}

export function CatalogItemSheet({
  open,
  onClose,
  ...form
}: {
  open: boolean
  onClose(): void
} & Omit<FormProps, 'onDone'>) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={form.item ? 'Upravit položku' : 'Přidat do šablony'}
      subtitle={form.item?.name}
    >
      {/* Sheet vykresluje obsah jen otevreny, takze se koncept plni pri kazdem otevreni. */}
      <CatalogItemForm key={form.item?.id ?? 'nova'} {...form} onDone={onClose} />
    </Sheet>
  )
}
